import { MANDATED_REPORTS } from "./data";
import { C } from "./theme";
import { Callout, Card, Eyebrow, Pullquote, Section, Source, Step, SubSection, Table, Td } from "./ui";

// ===================================================================
// 12. Additional feature for SDPD: end-of-year reports
//
// The proposal in this section is custom development, not shipped
// functionality, and is written as such throughout. The statutory
// citations in data.ts were verified against the statutes; the required
// fields listed there are quoted closely because a procurement reader
// will check them.
// ===================================================================

export function CustomBuildSection() {
  return (
    <Section
      id="custom"
      number="12"
      title="Additional feature for SDPD: end-of-year reports"
      subtitle={
        <>
          We know that putting end-of-year reports together is tedious and expensive.
          Analysts spend weeks reading finished reports back out by hand to fill in
          spreadsheets, against a statutory deadline. This section describes something we
          would build for SDPD specifically, because that work is largely avoidable: the
          data those reports need is already in the narratives officers write.
        </>
      }
    >
      <SubSection
        title="The year-end reporting problem"
        subtitle="California requires SDPD to publish and submit several statistical reports every year. The data in all of them is already written down. It is just written down in prose."
      >
        <p className="text-sm text-slate-700 leading-relaxed max-w-3xl">
          An officer documenting a use-of-force incident already records what force was
          applied, what equipment was involved, who was present, and what happened. Months
          later, an analyst opens that same report and reads the facts back out by hand to
          populate a spreadsheet, because the report is a narrative and the mandate needs a
          table.
        </p>

        <div className="mt-5">
          <Table
            minWidth={720}
            head={["Authority", "Report", "What it requires", "Cadence"]}
          >
            {MANDATED_REPORTS.map((r) => (
              <tr key={r.cite}>
                <Td strong className="whitespace-nowrap font-mono text-xs">
                  {r.cite}
                </Td>
                <Td strong>
                  {r.name}
                  <div className="text-[11px] font-normal text-slate-500 mt-0.5">
                    To {r.goesTo}
                  </div>
                </Td>
                <Td>
                  <ul className="space-y-1">
                    {r.requires.map((x) => (
                      <li key={x} className="flex gap-2 leading-relaxed">
                        <span
                          className="shrink-0 w-1 h-1 rounded-full mt-[7px]"
                          style={{ backgroundColor: C.primary }}
                        />
                        <span>{x}</span>
                      </li>
                    ))}
                  </ul>
                </Td>
                <Td className="text-xs">{r.cadence}</Td>
              </tr>
            ))}
          </Table>
        </div>

        <p className="text-[11px] text-slate-500 mt-2 leading-relaxed max-w-3xl">
          This is not the complete list. It excludes NIBRS submissions, reporting to the
          Commission on Police Practices, City Council requests, grant reporting, and the
          ad hoc statistical questions a department of this size fields continuously.
        </p>
      </SubSection>

      <Pullquote>
        The department writes the data down once, then pays to have it read back out.
      </Pullquote>

      <SubSection
        title="What we would build"
        subtitle="Structured capture at the moment the report is written, confirmed by the officer who was there, rather than reconstructed by an analyst the following spring."
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
          <Step n={1} title="Configure the fields">
            SDPD defines what it needs to count, mapped to the statutory schemas above and
            to any internal reporting the department runs.
          </Step>
          <Step n={2} title="Surface at report time">
            When a report describes a reportable event, the relevant fields appear
            alongside the narrative, pre-populated from what the officer already said.
          </Step>
          <Step n={3} title="Officer confirms" color={C.accent}>
            The officer verifies or corrects each field before the report is exported. It
            is part of the same review they already do, not a second form.
          </Step>
          <Step n={4} title="Query it any time" color={C.good}>
            Command staff and analysts filter, aggregate, and export against structured
            data continuously rather than assembling it once a year.
          </Step>
        </div>

        <div className="mt-6">
          <ExtractionMock />
        </div>
      </SubSection>

      <SubSection
        title="Two design commitments we would make up front"
        subtitle="Both matter more than the feature itself, and we would want them written into the contract."
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-lg border-2 border-amber-200 bg-amber-50/50 p-5">
            <Eyebrow>The officer is the source, not the model</Eyebrow>
            <p className="text-sm text-slate-700 leading-relaxed">
              Protected characteristics are never inferred. RIPA is explicit that the data
              is the <em>officer's</em> perception, and a model guessing someone's race
              from narrative text would be both unlawful as a data source and indefensible
              in a disparity analysis. The system presents the field; the officer answers
              it. Where a value is drawn from what the officer already wrote, it is shown
              as a proposal they must confirm.
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 p-5">
            <Eyebrow>Every value traces to its sentence</Eyebrow>
            <p className="text-sm text-slate-700 leading-relaxed">
              Each captured field links back to the passage of the report it came from and
              to the officer who confirmed it. When a published figure is challenged, and
              at some point one will be, the department can produce the underlying reports
              rather than defending a spreadsheet whose provenance nobody remembers.
            </p>
          </div>
        </div>
      </SubSection>

      <SubSection
        title="What changes for the people doing this work"
        subtitle="The reporting obligation does not go away. The three-month scramble to satisfy it does."
      >
        <div className="grid md:grid-cols-2 gap-4">
          <Card title="Today">
            Analysts pull a year of reports, read them, hand-code the fields, reconcile
            disagreements, and assemble the submission against a statutory deadline. Errors
            surface late, and correcting one means going back to the source reports.
          </Card>
          <Card title="With structured capture">
            The submission is a query. It can be run in March, in July, or the week before
            it is due, and it returns the same answer. A discrepancy is traceable to the
            report and the officer that produced it.
          </Card>
        </div>

        <div className="mt-5">
          <Callout tone="good" title="What command staff get out of it">
            <p>
              Command staff stop waiting for the annual report to learn what the annual
              report will say. Force trends, equipment usage, and stop demographics become
              things the department can look at in the current quarter, while there is
              still time to act on them, rather than findings that arrive a year after the
              conduct they describe.
            </p>
          </Callout>
        </div>
      </SubSection>

      <SubSection
        title="How we would scope it"
        subtitle="This is custom development, and it is work we would love to build for the San Diego Police Department."
      >
        <p className="text-sm text-slate-700 leading-relaxed max-w-3xl">
          The extraction and reporting capability described here is not shipped
          functionality today. It is what we propose to build for San Diego, and we would
          scope it jointly rather than guess at it: SDPD names the reports it must produce
          and the fields each one needs, we build against those schemas, and the department
          validates the output against a period it has already reported manually. Only once
          the two agree would it replace any part of the existing process.
        </p>
        <p className="text-sm text-slate-700 leading-relaxed max-w-3xl mt-3">
          We would treat this as part of the engagement rather than as a separately priced
          module. The pricing in Section 14 is the whole price.
        </p>
      </SubSection>

      <Source>
        Statutory requirements as cited: Gov. Code § 12525.2 (use of force incident
        reporting, added by AB 71); Gov. Code § 12525.5 (Racial and Identity Profiling Act
        stop data, AB 953); AB 481, Gov. Code § 7072 (annual military equipment report).
        Field lists are drawn from the statutory text.
      </Source>
    </Section>
  );
}

// ===================================================================
// Mock of structured capture alongside the narrative
// ===================================================================

const FIELDS: {
  label: string;
  value: string;
  state: "proposed" | "officer";
  from?: string;
}[] = [
  { label: "Force applied", value: "Control hold, takedown", state: "proposed", from: "narrative" },
  { label: "Weapon or equipment used", value: "None discharged", state: "proposed", from: "narrative" },
  { label: "Injury level", value: "No visible injury", state: "proposed", from: "narrative" },
  { label: "Officers involved", value: "2", state: "proposed", from: "narrative" },
  { label: "Perceived race or ethnicity", value: "Officer entry required", state: "officer" },
  { label: "Perceived gender", value: "Officer entry required", state: "officer" },
  { label: "Approximate age", value: "Officer entry required", state: "officer" },
];

function ExtractionMock() {
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden">
      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">
        {/* Narrative side */}
        <div className="p-5">
          <Eyebrow>The report the officer wrote</Eyebrow>
          <div className="text-[13px] text-slate-700 leading-relaxed space-y-2">
            <p>
              …I directed the subject to place his hands behind his back. He pulled his
              right arm away and turned toward me.{" "}
              <mark className="bg-blue-100 rounded px-0.5">
                I applied a control hold to his right wrist and, with Officer Nguyen,
                brought him to the ground
              </mark>{" "}
              in a prone position, where he was handcuffed without further resistance.
            </p>
            <p>
              <mark className="bg-blue-100 rounded px-0.5">
                No weapon was drawn or discharged at any point during the contact.
              </mark>{" "}
              I examined the subject for injury and observed none. Paramedics were not
              requested and he declined medical attention.
            </p>
          </div>
        </div>

        {/* Structured side */}
        <div className="p-5 bg-slate-50">
          <div className="flex items-baseline justify-between gap-3 mb-3">
            <Eyebrow>Reportable fields, for confirmation</Eyebrow>
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
              § 12525.2
            </span>
          </div>
          <div className="space-y-2">
            {FIELDS.map((f) => (
              <div
                key={f.label}
                className={`rounded-md border px-3 py-2 ${
                  f.state === "officer"
                    ? "border-amber-300 bg-amber-50"
                    : "border-slate-200 bg-white"
                }`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                    {f.label}
                  </span>
                  {f.state === "proposed" ? (
                    <span className="text-[9px] uppercase tracking-wider font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1 py-px shrink-0">
                      From narrative
                    </span>
                  ) : (
                    <span className="text-[9px] uppercase tracking-wider font-bold text-amber-800 bg-amber-100 border border-amber-300 rounded px-1 py-px shrink-0">
                      Officer only
                    </span>
                  )}
                </div>
                <div
                  className={`text-sm mt-0.5 ${
                    f.state === "officer"
                      ? "text-amber-800 italic"
                      : "text-slate-900 font-medium"
                  }`}
                >
                  {f.value}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
            Blue fields are proposed from the passages highlighted at left and must be
            confirmed. Amber fields are never inferred and are answered by the officer.
          </p>
        </div>
      </div>
      <div className="border-t border-slate-200 px-5 py-2.5 bg-white text-[11px] text-slate-500">
        Illustrative mock of proposed functionality. Field sets would be configured to
        SDPD's reporting schemas.
      </div>
    </div>
  );
}
